/**
 * "Show me" (1.4.2): after Ask answers a how-do-I question, this goes to the screen, scrolls the control
 * into view, rings it in the highlight colour for 3 seconds with a "Here" label beside it (never colour
 * alone), and moves keyboard focus to it so a screen reader announces it. It only navigates and points;
 * it never presses anything.
 */
import type { AssistantShowMe } from '@shared/assistant';

export const SHOW_ME_MS = 3000;
const FIND_FOR_MS = 5000;

const visible = (el: Element): el is HTMLElement => el instanceof HTMLElement && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';

/** The first target present and visible on the page (a selector may match several; the visible one wins). */
export function findTarget(targets: readonly string[], root: ParentNode = document): HTMLElement | null {
  for (const sel of targets) {
    let found: HTMLElement | null = null;
    try {
      found = [...root.querySelectorAll(sel)].find(visible) ?? null;
    } catch {
      found = null;
    }
    if (found) return found;
  }
  return null;
}

/** Ring, label and focus on one element; undone after `ms`. Returns the undo. */
export function highlight(el: HTMLElement, hereText: string, ms = SHOW_ME_MS): () => void {
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: reduced ? 'auto' : 'smooth' });
  el.setAttribute('data-showme-on', '');
  const addedTabIndex = !el.matches('a[href], button, input, select, textarea, [tabindex]');
  if (addedTabIndex) el.setAttribute('tabindex', '-1');
  const label = document.createElement('span');
  label.className = 'showme-here';
  label.setAttribute('aria-hidden', 'true');
  label.textContent = hereText;
  label.dataset.testid = 'showme-here';
  document.body.appendChild(label);
  const place = () => {
    const r = el.getBoundingClientRect();
    label.style.top = `${Math.max(4, r.top - 30)}px`;
    label.style.left = `${Math.max(4, Math.min(r.left, window.innerWidth - label.offsetWidth - 4))}px`;
  };
  place();
  window.addEventListener('scroll', place, true);
  window.addEventListener('resize', place);
  el.focus({ preventScroll: true });
  // a closing panel hands focus back to its button a moment later: take it back once
  const refocus = window.setTimeout(() => {
    if (document.activeElement !== el && el.isConnected) el.focus({ preventScroll: true });
  }, 350);
  let done = false;
  const undo = () => {
    if (done) return;
    done = true;
    window.clearTimeout(refocus);
    window.removeEventListener('scroll', place, true);
    window.removeEventListener('resize', place);
    el.removeAttribute('data-showme-on');
    if (addedTabIndex && document.activeElement !== el) el.removeAttribute('tabindex');
    label.remove();
  };
  window.setTimeout(undo, ms);
  return undo;
}

/** Navigate, wait for the control to appear, then point at it. `onFound` gets whether it was found. */
export function runShowMe(target: AssistantShowMe, navigate: (to: string) => void, hereText: string, onFound?: (found: boolean) => void): void {
  const here = window.location.pathname;
  // 1.7.9: a control of the open event is pointed at where the asker already is
  const stay = (target.stayOn ?? []).some((p) => {
    try {
      return new RegExp(p).test(here);
    } catch {
      return false;
    }
  });
  if (!stay && here !== target.route) navigate(target.route);
  const started = Date.now();
  const tick = () => {
    const el = findTarget(target.targets) ?? (Date.now() - started > FIND_FOR_MS ? document.querySelector<HTMLElement>('main h1') : null);
    if (el) {
      highlight(el, hereText);
      onFound?.(findTarget(target.targets) === el);
      return;
    }
    window.setTimeout(tick, 120);
  };
  // after the page has rendered and the shell has moved focus to its heading
  window.setTimeout(tick, 250);
}
